import { Fragment, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useCashShifts, useCashShiftBreakdown, PAYMENT_METHOD_LABEL, PaymentMethod } from "@/api/pos";

const currency = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 });

/**
 * Historial y conciliación de cierres de caja (todas las sucursales/turnos).
 * El backend y el hook (`useCashShifts`) ya existían desde el módulo de POS
 * (ver pos.service.ts::listCashShifts) — hasta ahora la diferencia de un
 * cierre (efectivo esperado vs contado) solo se veía una vez en un toast al
 * cerrar el turno y se perdía. Esta página la deja consultable siempre,
 * con quién abrió y quién cerró cada turno — base para la conciliación de
 * caja.
 */
export default function CashShiftsPage() {
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { data, isLoading } = useCashShifts(page, 20);
  const shifts = data?.data ?? [];
  const meta = data?.meta;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Cierres de caja</h1>
        <p className="text-sm text-gray-500">Historial de turnos por sucursal: efectivo inicial, esperado, contado y la diferencia (sobrante/faltante) de cada cierre.</p>
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Sucursal</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Abierto</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Cerrado</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Efectivo inicial</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Esperado</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Contado</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Diferencia</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Estado</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {!isLoading && shifts.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-center text-gray-400">
                  Todavía no se han registrado turnos de caja.
                </td>
              </tr>
            )}
            {shifts.map((s) => {
              const diff = s.difference !== null ? Number(s.difference) : null;
              const isExpanded = expandedId === s.id;
              return (
                <Fragment key={s.id}>
                  <tr className="cursor-pointer hover:bg-gray-50" onClick={() => setExpandedId(isExpanded ? null : s.id)}>
                    <td className="px-4 py-3 text-gray-600">{s.branch?.name ?? "—"}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {new Date(s.openedAt).toLocaleString("es-CO")}
                      <div className="text-xs text-gray-400">{s.openedBy?.fullName ?? "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {s.closedAt ? new Date(s.closedAt).toLocaleString("es-CO") : "—"}
                      {s.closedBy && <div className="text-xs text-gray-400">{s.closedBy.fullName}</div>}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">{currency.format(Number(s.openingCash))}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{s.expectedCash !== null ? currency.format(Number(s.expectedCash)) : "—"}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{s.closingCash !== null ? currency.format(Number(s.closingCash)) : "—"}</td>
                    <td className={`px-4 py-3 text-right font-medium ${diff === null ? "text-gray-400" : diff === 0 ? "text-gray-600" : diff > 0 ? "text-blue-600" : "text-red-600"}`}>
                      {diff === null ? "—" : `${diff > 0 ? "+" : ""}${currency.format(diff)}${diff !== 0 ? (diff > 0 ? " (sobrante)" : " (faltante)") : ""}`}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                          s.status === "ABIERTO" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"
                        }`}
                      >
                        {s.status === "ABIERTO" ? "Abierto" : "Cerrado"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-400">{isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={9} className="bg-gray-50 px-4 py-3">
                        <ShiftBreakdownDetail shiftId={s.id} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>

        {meta && meta.totalPages && meta.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm">
            <span className="text-gray-500">
              Página {meta.page} de {meta.totalPages} ({meta.total} turnos)
            </span>
            <div className="space-x-2">
              <button className="btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Anterior
              </button>
              <button className="btn-secondary" disabled={!!meta.totalPages && page >= meta.totalPages} onClick={() => setPage((p) => p + 1)}>
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Desglose por método de pago de un turno puntual — se carga solo al expandir la fila (ver pos.service.ts::getCashShiftBreakdown). */
function ShiftBreakdownDetail({ shiftId }: { shiftId: string }) {
  const { data: breakdown, isLoading } = useCashShiftBreakdown(shiftId);

  if (isLoading) return <p className="text-sm text-gray-400">Cargando desglose...</p>;
  if (!breakdown || breakdown.byPaymentMethod.length === 0) return <p className="text-sm text-gray-400">Sin ventas registradas en este turno.</p>;

  return (
    <div className="max-w-md">
      <p className="mb-1.5 text-xs font-semibold uppercase text-gray-500">Desglose por método de pago</p>
      <table className="w-full text-sm">
        <tbody>
          {breakdown.byPaymentMethod.map((row) => (
            <tr key={row.paymentMethod} className="border-b border-gray-100 last:border-0">
              <td className="py-1 text-gray-600">{PAYMENT_METHOD_LABEL[row.paymentMethod as PaymentMethod] ?? row.paymentMethod}</td>
              <td className="py-1 text-right font-medium text-gray-900">{currency.format(row.salesTotal)}</td>
              <td className="py-1 text-right text-xs text-red-500">{row.refundsTotal > 0 ? `-${currency.format(row.refundsTotal)} dev.` : ""}</td>
            </tr>
          ))}
          <tr>
            <td className="pt-1.5 text-sm font-semibold text-gray-700">Total vendido</td>
            <td className="pt-1.5 text-right text-sm font-semibold text-gray-900">{currency.format(breakdown.grandSalesTotal)}</td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
